import { authService, PermissionScope } from '@/services/AuthService';
import { Partner, PartnerService } from '@/services/PartnerService';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { RESOURCES } from '@/constants/resources';

interface PartnerContextType {
  impersonatedPartnerId: number | null;
  setImpersonatedPartner: (id: number | null) => void;
  clearImpersonatedPartner: () => void;

  partners: Partner[];
  setPartners: React.Dispatch<React.SetStateAction<Partner[]>>;
  refreshPartners: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);
const STORAGE_KEY = 'impersonated_partner_id';

export const getImpersonatedPartnerId = (): number | null => {
  const value = sessionStorage.getItem(STORAGE_KEY);
  if (value == null) return null;

  const parsed = parseInt(value);
  return isNaN(parsed) ? null : parsed;
};

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [impersonatedPartnerId, setPartnerIdState] = useState<number | null>(() => {
    return getImpersonatedPartnerId();
  });

  const setImpersonatedPartner = (id: number | null) => {
    setPartnerIdState(id);
    if (id) {
      sessionStorage.setItem(STORAGE_KEY, id.toString());
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  };

  const clearImpersonatedPartner = () => {
    setImpersonatedPartner(null);
  };

  const refreshPartners = useCallback(async () => {
    if (!authService.hasPermission(RESOURCES.PARTNERS, PermissionScope.READ)) {
      setPartners([])
      return;
    }

    try {
      const data = (await PartnerService.getAll()).partners;
      setPartners(data);
      console.log("Refreshing partners list...");
    } catch (error) {
      console.error("Failed to refresh partners:", error);
    }
  }, []);

  useEffect(() => {
    refreshPartners();
  }, [refreshPartners]); 

  return (
    <PartnerContext.Provider value={{ 
      impersonatedPartnerId, 
      setImpersonatedPartner, 
      clearImpersonatedPartner,
      partners, 
      setPartners,
      refreshPartners
    }}>
      {children}
    </PartnerContext.Provider>
  );
};

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
};
